/* =========================================================================
   Settings → Site identity / App store links / Contact / Social.

   Each section saves on its own (POST /config with just that section's
   fields; the backend validates and answers 422 + a per-field `errors` map).
   Values are validated inline first with the same rules as the backend.

   RBAC: staff without settings.edit see every value read-only, and no Save
   button (the backend also refuses the write with 403).
   ========================================================================= */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaApple, FaGooglePlay, FaAmazon, FaFacebook, FaInstagram, FaYoutube, FaLinkedin, FaTiktok, FaWhatsapp } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { SiRoku, SiAppletv, SiSamsung, SiLg } from "react-icons/si";
import { MdEmail, MdPhone, MdLocationOn, MdOpenInNew, MdFileUpload } from "react-icons/md";
import styles from "./Settings.module.css";
import { postSettingsSubscription, UploadImage } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { useRbac } from "../../rbac/RbacContext";
import { useTenant } from "../../context/TenantContext";
import useUnsavedChangesGuard, { UNSAVED_MESSAGE } from "./useUnsavedChangesGuard";
import {
  APP_INFO_SECTIONS,
  fromConfig,
  isHttpsUrl,
  maxLengthFor,
  prefillFromTenant,
  toPayload,
  validateAppInfoField,
  validateAppInfoValues,
} from "./appInfoFields";

const ICONS = {
  ios: FaApple,
  android: FaGooglePlay,
  roku: SiRoku,
  firetv: FaAmazon,
  appletv: SiAppletv,
  samsung: SiSamsung,
  lg: SiLg,
  email: MdEmail,
  phone: MdPhone,
  whatsapp: FaWhatsapp,
  address: MdLocationOn,
  facebook: FaFacebook,
  instagram: FaInstagram,
  x: FaXTwitter,
  youtube: FaYoutube,
  linkedin: FaLinkedin,
  tiktok: FaTiktok,
};

export { UNSAVED_MESSAGE };

// Raster only: the URL is rendered on the public site, and an SVG can carry script.
// Client-side guard only -- /image-upload itself accepts any image/* type.
const UPLOAD_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/x-icon,image/vnd.microsoft.icon";
const UPLOAD_MAX_BYTES = 2 * 1024 * 1024;

// `sectionKey` (optional): render just that section, without the tab strip --
// the Settings sub-menu picks it. Pass null to keep the form mounted (and its
// unsaved edits) while another sub-menu item is showing.
const AppInfoSettings = ({ config, onSaved, sectionKey, onDirtyChange }) => {
  const { can } = useRbac();
  const canEdit = can("settings", "edit");
  // The upload goes through /image-upload, which also needs media:create.
  const canUpload = canEdit && can("media", "create");

  const { tenant } = useTenant() || {};
  const tenantName = tenant?.name;
  const tenantLogo = tenant?.logoUrl;
  const tenantEmail = tenant?.contactEmail;
  // Keyed on the primitive fields so the tenant-info refetch (a new object with
  // the same values) doesn't re-seed the form and wipe edits.
  const prefill = useMemo(
    () => (canEdit ? prefillFromTenant({ name: tenantName, logoUrl: tenantLogo, contactEmail: tenantEmail }, config) : {}),
    [canEdit, tenantName, tenantLogo, tenantEmail, config]
  );

  const [saved, setSaved] = useState(() => fromConfig(config));
  const [values, setValues] = useState(() => ({ ...fromConfig(config), ...prefill }));
  const [errors, setErrors] = useState({});
  const [savingSection, setSavingSection] = useState(null);
  const [uploadingField, setUploadingField] = useState(null);
  const [activeKey, setActiveKey] = useState(APP_INFO_SECTIONS[0].key);

  // Re-seed when the config arrives (or is reloaded) from /config.
  // `prefill` is derived from `config`, so it is already current here.
  const prefillRef = useRef(prefill);
  useEffect(() => {
    const next = fromConfig(config);
    prefillRef.current = prefill;
    setSaved(next);
    setValues({ ...next, ...prefill });
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefill-only changes are merged below
  }, [config]);

  // Tenant info / permissions can land after the form is on screen. Swap in the
  // new suggestions only where a field still shows the old suggestion or is
  // empty, so anything staff typed meanwhile is kept. A dropped suggestion
  // falls back to the saved value.
  useEffect(() => {
    const prev = prefillRef.current;
    if (prev === prefill) return;
    prefillRef.current = prefill;
    setValues((cur) => {
      const out = { ...cur };
      new Set([...Object.keys(prev), ...Object.keys(prefill)]).forEach((n) => {
        if (cur[n] === "" || cur[n] === prev[n]) out[n] = prefill[n] ?? saved[n] ?? "";
      });
      return out;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `saved` is read, not reacted to
  }, [prefill]);

  // A suggestion left as-is is not an "unsaved edit" (no leave warning, no
  // dirty dot), but it still enables Save so staff can keep it in one click.
  const isUntouchedPrefill = (n) =>
    prefill[n] !== undefined && values[n] === prefill[n] && String(saved[n] ?? "").trim() === "";
  const dirtyFields = Object.keys(values).filter(
    (n) => String(values[n]).trim() !== String(saved[n]).trim() && !isUntouchedPrefill(n)
  );
  const isSectionDirty = (section) => section.fields.some((f) => dirtyFields.includes(f.name));
  const hasPrefill = (section) => section.fields.some((f) => isUntouchedPrefill(f.name));
  const hasUnsaved = canEdit && dirtyFields.length > 0;

  useUnsavedChangesGuard(hasUnsaved);

  const dirtySectionKeys = APP_INFO_SECTIONS.filter((s) => canEdit && isSectionDirty(s))
    .map((s) => s.key)
    .join(",");
  useEffect(() => {
    onDirtyChange?.(dirtySectionKeys ? dirtySectionKeys.split(",") : []);
  }, [dirtySectionKeys, onDirtyChange]);

  const handleChange = useCallback((name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    // Inline validation as you type (clears server errors for that field too).
    setErrors((prev) => {
      const msg = validateAppInfoField(name, value);
      const next = { ...prev };
      if (msg) next[name] = msg;
      else delete next[name];
      return next;
    });
  }, []);

  // Uploads to the tenant's Bunny CDN (same /image-upload the media library
  // uses) and puts the CDN URL in the field; the section's Save persists it.
  const handleUpload = async (field, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!UPLOAD_ACCEPT.split(",").includes(file.type)) {
      showToast("Please choose a PNG, JPG, WebP, GIF or ICO image.", "error");
      return;
    }
    if (file.size > UPLOAD_MAX_BYTES) {
      showToast("Image must be 2 MB or smaller.", "error");
      return;
    }
    setUploadingField(field.name);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("folder", "branding");
      const res = await UploadImage(fd);
      const url = res?.data?.data?.upload?.file_url || res?.data?.data?.url || res?.data?.url || "";
      if (!url) {
        showToast("Upload finished but no image URL came back.", "error");
        return;
      }
      handleChange(field.name, url);
      showToast(`${field.label.replace(/ URL$/, "")} uploaded — click Save to apply it.`, "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "Image upload failed.", "error");
    } finally {
      setUploadingField(null);
    }
  };

  const handleSave = async (section) => {
    const names = section.fields.map((f) => f.name);
    const clientErrors = validateAppInfoValues(values, names);
    if (Object.keys(clientErrors).length) {
      setErrors((prev) => ({ ...prev, ...clientErrors }));
      showToast("Please fix the highlighted fields.", "error");
      return;
    }

    const payload = toPayload(values, names);
    setSavingSection(section.key);
    try {
      const res = await postSettingsSubscription(payload);
      const savedRow = res?.data || {};
      const nextSaved = Object.fromEntries(names.map((n) => [n, savedRow[n] ?? payload[n] ?? ""]));
      setSaved((prev) => ({ ...prev, ...nextSaved }));
      setValues((prev) => ({ ...prev, ...nextSaved }));
      setErrors((prev) => {
        const next = { ...prev };
        names.forEach((n) => delete next[n]);
        return next;
      });
      showToast(`${section.title} saved.`, "success");
      onSaved?.(savedRow);
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 422 && data?.errors) {
        setErrors((prev) => ({ ...prev, ...data.errors }));
        showToast(data.message || "Some fields are invalid.", "error");
      } else {
        showToast(data?.message || `Could not save ${section.title.toLowerCase()}.`, "error");
      }
    } finally {
      setSavingSection(null);
    }
  };

  const renderField = (field) => {
    const value = values[field.name] ?? "";
    const error = errors[field.name];
    const max = maxLengthFor(field);
    const Icon = field.icon ? ICONS[field.icon] : null;
    const id = `appinfo-${field.name}`;
    const inputProps = {
      id,
      name: field.name,
      value,
      placeholder: field.placeholder,
      onChange: (e) => handleChange(field.name, e.target.value),
      readOnly: !canEdit,
      disabled: !canEdit,
      "aria-invalid": Boolean(error),
      "aria-describedby": error ? `${id}-error` : undefined,
      className: `${styles.textInput} ${error ? styles.inputError : ""}`,
    };
    const trimmed = String(value).trim();

    return (
      <div key={field.name} className={styles.formRow}>
        <label htmlFor={id} className={styles.formLabel}>
          {Icon && <Icon className={styles.fieldIcon} aria-hidden="true" />}
          {field.label}
        </label>
        <div className={styles.formControl}>
          <div className={styles.inputLine}>
            {field.type === "textarea" ? (
              <textarea rows={3} {...inputProps} />
            ) : (
              <input type={field.type === "email" ? "email" : field.type === "url" ? "url" : "text"} {...inputProps} />
            )}
            {field.upload && canUpload && (
              <label className={`${styles.uploadBtn} ${uploadingField ? styles.uploadBtnBusy : ""}`}>
                <input
                  type="file"
                  accept={UPLOAD_ACCEPT}
                  className={styles.visuallyHidden}
                  disabled={Boolean(uploadingField)}
                  onChange={(e) => handleUpload(field, e)}
                  aria-label={`Upload ${field.label.replace(/ URL$/, "").toLowerCase()}`}
                />
                <MdFileUpload aria-hidden="true" />
                {uploadingField === field.name ? "Uploading…" : "Upload"}
              </label>
            )}
            {field.openLink && (
              isHttpsUrl(trimmed) ? (
                <a href={trimmed} target="_blank" rel="noopener noreferrer" className={styles.openLink}>
                  Open <MdOpenInNew aria-hidden="true" />
                </a>
              ) : (
                <span className={`${styles.openLink} ${styles.openLinkDisabled}`} aria-disabled="true">
                  Open <MdOpenInNew aria-hidden="true" />
                </span>
              )
            )}
          </div>
          <div className={styles.fieldMeta}>
            {error ? (
              <span id={`${id}-error`} role="alert" className={styles.errorText}>{error}</span>
            ) : isUntouchedPrefill(field.name) ? (
              <span className={styles.prefillHint}>Filled in from your account — edit if needed, then Save.</span>
            ) : (
              <span />
            )}
            {field.counter && max && (
              <span className={`${styles.counter} ${trimmed.length > max ? styles.counterOver : ""}`}>
                {trimmed.length}/{max}
              </span>
            )}
          </div>
          {field.preview && isHttpsUrl(trimmed) && !error && (
            <img src={trimmed} alt={`${field.label} preview`} className={styles.imagePreview} />
          )}
        </div>
      </div>
    );
  };

  const controlled = sectionKey !== undefined;
  const active = controlled
    ? APP_INFO_SECTIONS.find((s) => s.key === sectionKey)
    : APP_INFO_SECTIONS.find((s) => s.key === activeKey) || APP_INFO_SECTIONS[0];
  if (!active) return null;

  return (
    <div className={styles.appInfo}>
      {!controlled && (
        <div className={styles.appInfoTabs} role="tablist" aria-label="Site and app info">
          {APP_INFO_SECTIONS.map((section) => (
            <button
              key={section.key}
              type="button"
              role="tab"
              aria-selected={section.key === active.key}
              className={`${styles.appInfoTab} ${section.key === active.key ? styles.appInfoTabActive : ""}`}
              onClick={() => setActiveKey(section.key)}
            >
              {section.title}
              {canEdit && isSectionDirty(section) && <span className={styles.dirtyDot} aria-label="unsaved changes" />}
            </button>
          ))}
        </div>
      )}

      <section className={styles.card} role={controlled ? "region" : "tabpanel"} aria-label={active.title}>
        <h3 className={styles.sectionHeading}>{active.title}</h3>
        {!canEdit && (
          <p className={styles.readOnlyNote}>You have view-only access to Settings.</p>
        )}
        {active.fields.map(renderField)}
        {canEdit && (
          <div className={styles.sectionActions}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => handleSave(active)}
              disabled={savingSection === active.key || !(isSectionDirty(active) || hasPrefill(active))}
            >
              {savingSection === active.key ? "Saving…" : `Save ${active.title.toLowerCase()}`}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default AppInfoSettings;
